import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import type { Profile } from '@/types';
import MemberStatCard from './components/MemberStatCard';
import MemberRow from './components/MemberRow';

type FilterKey = 'pending' | 'approved' | 'rejected' | 'all';

const tabs: { key: FilterKey; label: string }[] = [
  { key: 'pending', label: '승인 대기' },
  { key: 'approved', label: '승인 완료' },
  { key: 'rejected', label: '승인 거절' },
  { key: 'all', label: '전체' },
];

export default function MembersPage() {
  const [members, setMembers] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [filter, setFilter] = useState<FilterKey>('pending');
  const [query, setQuery] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [confirmBulk, setConfirmBulk] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data, error: fetchError } = await supabase
        .from('profiles')
        .select('*')
        .order('created_at', { ascending: false });
      if (fetchError) throw fetchError;
      setMembers((data as Profile[]) ?? []);
    } catch {
      setError('회원 목록을 불러오지 못했어요.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!notice) return;
    const t = setTimeout(() => setNotice(''), 2500);
    return () => clearTimeout(t);
  }, [notice]);

  const updateStatus = async (id: string, status: Profile['status']) => {
    setBusyId(id);
    setError('');
    try {
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ status })
        .eq('id', id);
      if (updateError) throw updateError;
      setMembers((prev) => prev.map((m) => (m.id === id ? { ...m, status } : m)));
      setNotice(
        status === 'approved'
          ? '회원을 승인했어요.'
          : status === 'rejected'
            ? '회원을 거절했어요.'
            : '승인 대기 상태로 되돌렸어요.',
      );
    } catch {
      setError('상태 변경에 실패했어요.');
    } finally {
      setBusyId(null);
    }
  };

  const counts = useMemo(
    () => ({
      pending: members.filter((m) => m.status === 'pending').length,
      approved: members.filter((m) => m.status === 'approved').length,
      rejected: members.filter((m) => m.status === 'rejected').length,
      all: members.length,
    }),
    [members],
  );

  const pendingMembers = useMemo(
    () => members.filter((m) => m.status === 'pending' && m.role !== 'admin'),
    [members],
  );

  const approveAll = async () => {
    setConfirmBulk(false);
    if (pendingMembers.length === 0) return;
    setBulkBusy(true);
    setError('');
    try {
      const ids = pendingMembers.map((m) => m.id);
      const { error: bulkError } = await supabase
        .from('profiles')
        .update({ status: 'approved' })
        .in('id', ids);
      if (bulkError) throw bulkError;
      setMembers((prev) =>
        prev.map((m) => (ids.includes(m.id) ? { ...m, status: 'approved' } : m)),
      );
      setNotice(`${ids.length}명의 회원을 모두 승인했어요.`);
    } catch {
      setError('일괄 승인에 실패했어요.');
    } finally {
      setBulkBusy(false);
    }
  };

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return members.filter((m) => {
      const matchesFilter = filter === 'all' || m.status === filter;
      if (!matchesFilter) return false;
      if (!q) return true;
      return (
        (m.name ?? '').toLowerCase().includes(q) || (m.email ?? '').toLowerCase().includes(q)
      );
    });
  }, [members, filter, query]);

  return (
    <div className="mx-auto w-full max-w-5xl px-4 py-8 md:px-6 md:py-10">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground-950">회원관리</h1>
          <p className="mt-1.5 text-sm text-foreground-600">
            가입 신청한 선생님을 확인하고 승인하면 서비스를 이용할 수 있어요.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="flex items-center gap-1.5 self-start rounded-md border border-background-300 px-3 py-2 text-sm font-medium text-foreground-700 transition-colors hover:bg-background-100 disabled:opacity-50 whitespace-nowrap"
        >
          <i className={`ri-refresh-line ${loading ? 'animate-spin' : ''}`}></i>
          새로고침
        </button>
      </div>

      <div className="mt-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <MemberStatCard
          label="전체 회원"
          value={counts.all}
          icon="ri-group-line"
          tone="neutral"
          active={filter === 'all'}
          onClick={() => setFilter('all')}
        />
        <MemberStatCard
          label="승인 대기"
          value={counts.pending}
          icon="ri-user-received-line"
          tone="accent"
          active={filter === 'pending'}
          onClick={() => setFilter('pending')}
        />
        <MemberStatCard
          label="승인 완료"
          value={counts.approved}
          icon="ri-user-follow-line"
          tone="primary"
          active={filter === 'approved'}
          onClick={() => setFilter('approved')}
        />
        <MemberStatCard
          label="승인 거절"
          value={counts.rejected}
          icon="ri-user-forbid-line"
          tone="secondary"
          active={filter === 'rejected'}
          onClick={() => setFilter('rejected')}
        />
      </div>

      <div className="mt-6 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="inline-flex gap-1 self-start rounded-full bg-background-100 p-1">
          {tabs.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setFilter(t.key)}
              className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors whitespace-nowrap ${
                filter === t.key
                  ? 'bg-primary-500 text-background-50'
                  : 'text-foreground-600 hover:text-foreground-900'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative">
            <span className="pointer-events-none absolute left-3 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center text-foreground-400">
              <i className="ri-search-line text-sm"></i>
            </span>
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="이름 또는 이메일 검색"
              className="w-full rounded-md border border-background-300 bg-background-50 py-2 pl-9 pr-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-400 sm:w-64"
            />
          </div>

          {pendingMembers.length > 0 && (
            <div className="relative">
              <button
                type="button"
                disabled={bulkBusy}
                onClick={() => setConfirmBulk((v) => !v)}
                className="flex items-center gap-1.5 rounded-md bg-accent-500 px-3 py-2 text-sm font-semibold text-background-50 transition hover:bg-accent-600 disabled:opacity-50 whitespace-nowrap"
              >
                <i className="ri-check-double-line"></i>
                대기 {pendingMembers.length}명 일괄 승인
              </button>

              {confirmBulk && (
                <div className="absolute right-0 z-20 mt-2 w-64 rounded-lg border border-background-200 bg-background-50 p-4 shadow-none">
                  <p className="text-sm text-foreground-800">
                    승인 대기 중인 <strong>{pendingMembers.length}명</strong>을 모두 승인할까요?
                  </p>
                  <div className="mt-3 flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => setConfirmBulk(false)}
                      className="rounded-md border border-background-300 px-3 py-1.5 text-xs font-semibold text-foreground-700 hover:bg-background-100 whitespace-nowrap"
                    >
                      취소
                    </button>
                    <button
                      type="button"
                      onClick={approveAll}
                      className="rounded-md bg-primary-500 px-3 py-1.5 text-xs font-semibold text-background-50 hover:bg-primary-600 whitespace-nowrap"
                    >
                      모두 승인
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {notice && (
        <div className="mt-4 flex items-center gap-2 rounded-md border border-primary-200 bg-primary-100/70 px-4 py-2.5 text-sm text-primary-700">
          <i className="ri-information-line"></i>
          {notice}
        </div>
      )}

      {error && !loading && (
        <div className="mt-4 flex items-center justify-between gap-3 rounded-md border border-accent-200 bg-accent-100/70 px-4 py-2.5 text-sm text-accent-800">
          <span className="flex items-center gap-2">
            <i className="ri-error-warning-line"></i>
            {error}
          </span>
          <button
            type="button"
            onClick={load}
            className="rounded-md border border-accent-300 px-3 py-1 text-xs font-semibold text-accent-800 hover:bg-accent-100 whitespace-nowrap"
          >
            다시 시도
          </button>
        </div>
      )}

      <div className="mt-4">
        {loading ? (
          <div className="flex items-center justify-center py-20 text-foreground-500">
            <i className="ri-loader-4-line mr-2 animate-spin text-xl"></i>
            불러오는 중...
          </div>
        ) : visible.length === 0 ? (
          <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed border-background-300 bg-background-100 py-16">
            <i className="ri-inbox-line text-3xl text-foreground-400"></i>
            <p className="text-sm text-foreground-600">
              {query ? '검색 결과가 없어요.' : '해당하는 회원이 없어요.'}
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {visible.map((m) => (
              <MemberRow
                key={m.id}
                member={m}
                busy={busyId === m.id}
                onApprove={(id) => updateStatus(id, 'approved')}
                onReject={(id) => updateStatus(id, 'rejected')}
                onReset={(id) => updateStatus(id, 'pending')}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}